import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BusinessComponent, BusinessComponentOptions, ColumnPanel, StaticParagraphComponent } from '@blk/simba';
import { ChartType, ColumnChartName } from '@ddt-web/chart';
import { EsgConstants } from '@ddt-web/config';
import { InjectConfig, PcConfigKey } from '@ddt-web/config-layer';
import { ComponentData, DisplayDataFactoryService, TabUtil } from '@ddt-web/data-service';
import { extractAsOfDateFromMultiplePortfolios } from '@ddt-web/esg';
import { DEFAULT_DISCLOSURE_TEXT_CLASSES, ReportUtils } from '@ddt-web/pdf-utils';
import * as isNil from 'lodash/isNil';
import { of } from 'rxjs';

import { DisclosureComponent } from '../disclaimers/disclosure-component';
import { HeadingComponent, HeadingLevel } from '../heading';
import { DefaultColumnComposition } from '../helpers/column-compositions/default-column-composition';
import { TableUtils } from '../helpers/utils/table-utils';

export class EsgRatingDistributionComponent extends BusinessComponent {
	private readonly chartTabId = 'esg.rating.distribution.chart';
	@InjectConfig(PcConfigKey.SHOW_AS_OF_DATE_DYNAMIC) public readonly showAsOfDateDynamic: boolean = EsgConstants.SHOW_AS_OF_DATE_DYNAMIC_DEFAULT;

	private filteredComponentData: ComponentData;

	constructor(
		name: string,
		dataComponentId: string,
		private disclosureTextClasses: string[] = DEFAULT_DISCLOSURE_TEXT_CLASSES,
		public visible: boolean = true,
		public isClientReport: boolean = false,
		public filteredPortfoliosWithoutIndexes?: string[],
		public displayDataFactoryService?: DisplayDataFactoryService
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
		if (this.filteredPortfoliosWithoutIndexes?.length) {
			this.displayDataFactoryService.getDisplayableComponentData(dataComponentId, of(this.filteredPortfoliosWithoutIndexes), new ContentService())
				.subscribe(res => {
					this.filteredComponentData = res;
				});
		}
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const chartDataTab = componentData?.getTabById(this.chartTabId);
		return componentData?.success && !isNil(TabUtil.getVisibleTabData(chartDataTab));
	}

	public buildComponent(componentData: ComponentData): AbstractRenderer<SVGGElement>[] {
		componentData = this.filteredPortfoliosWithoutIndexes?.length && this.filteredComponentData ? this.filteredComponentData : componentData;

		const columnPanels: AbstractRenderer<SVGGElement>[] = [];

		if (this.isComponentDataVisible(componentData)) {
			const chartData = componentData.getTabById(this.chartTabId);
			const columnCompositions = new DefaultColumnComposition().getColumnCompositions(chartData);

			const dcrKeyPrefix = ReportUtils.createDcrKeyPrefixForPdf(this.name);

			const asOfDate = extractAsOfDateFromMultiplePortfolios(componentData.getTabById('esg.asOfDate'));

			const description = this.isClientReport ? `${dcrKeyPrefix}DESCRIPTION_CLIENT_REPORT` : `${dcrKeyPrefix}DESCRIPTION`;

			const disclaimer = this.isClientReport ? `${dcrKeyPrefix}DISCLAIMER_CLIENT_REPORT` : `${dcrKeyPrefix}DISCLAIMER`;

			columnPanels.push(
				new ColumnPanel('ESG Rating Distribution Column', [
					// Create Title
					new HeadingComponent(`${dcrKeyPrefix}TITLE`, 'ESG Rating Distribution', HeadingLevel.H2),

					// Create Description
					new StaticParagraphComponent(description, description, {}, { paddingBottom: 10 }, ['text-10']),

					TableUtils.getTableAndChartHeaderRow('ESG Rating Distribution Header', chartData, columnCompositions),
					TableUtils.getTableAndChartBodyRow('ESG Rating Distribution Body', chartData, columnCompositions, ChartType.Column, 7, 0, ColumnChartName.GroupedColumnChart, {
						yAxis: {
							tickAmount: 5,
							labels: {
								format: '{value:1f}',
								formatter: null
							}
						},
						legend: {
							enabled: false
						}
					}),
					new DisclosureComponent(disclaimer, '', {}, { marginTop: 50 }, this.disclosureTextClasses),
					...(this.showAsOfDateDynamic ? [new DisclosureComponent('AS_OF_DATE_DISCLOSURE', 'As of: {0}', { 0: asOfDate }, {}, this.disclosureTextClasses)] : [])
				], { marginBottom: 30 }, Alignment.Horizontal.Left)
			);
		}
		return columnPanels;
	}
}
